/** Tipos das respostas da API de Oportunidades (B2). */
import type { Chave, Estagio, FaixasRegistro, SituacaoRegistro, Tipo } from '../../../backend/src/compartilhado/oportunidades.ts';
import type { TipoEntidade } from './tipos.ts';

export interface Registro {
  id: string;
  oportunidade_id: string;
  numero: string;
  parceiro_id: string | null;
  parceiro_nome: string | null;
  fabricante: string;
  fabricante_parceiro_id: string | null;
  data_registro: string;
  data_vencimento: string;
  situacao: SituacaoRegistro;
  substituido_por: string | null;
  observacao: string | null;
  /** Pacote 5 (0052): encerramento (nulos fora da situação "encerrado"). */
  encerrado_em?: string | null;
  motivo_encerramento?: string | null;
  observacao_encerramento?: string | null;
  atualizado_em?: string;
  historico?: { texto: string; data_hora: string; autor: string | null }[];
}
export interface OportunidadeResumo {
  id: string;
  numero: string;
  titulo: string;
  tipo: Tipo;
  estagio: Estagio;
  cliente: { id: string; nome: string; codigo: string };
  entidade: { id: string; tipo: TipoEntidade; razao_social: string; cnpj: string; codigo?: string | null };
  responsavel: { id: string; nome: string };
  /** null só em registro migrado sem valor comprovado: "Não determinado na origem" (nunca R$ 0,00). */
  valor_estimado: number | null;
  data_criacao: string;
  /** null só em Oportunidade encerrada migrada. */
  previsao_fechamento: string | null;
  data_encerramento: string | null;
  motivo_encerramento: string | null;
  observacao_encerramento?: string;
  proxima_acao: { descricao: string; data: string; responsavel: { id: string; nome: string } } | null;
  registros: Registro[];
  /** Versão vigente da Proposta (situação 'rascunho' = ainda não enviada ao Cliente). */
  proposta: null | { id: string; numero: string; versao: number; situacao: string; validade?: string | null };
  valor_vigente: number | null;
  margem: { bruta: number; percentual: number } | null;
  parceiros: { cotados: { id: string; nome: string }[]; selecionados: { id: string; nome: string }[] };
  pedidos: string[];
  /** valor do Pedido gerado (ganho parcial: só as quantidades ganhas) — D1 */
  valor_ganho?: number | null;
  /** Lixeira (12/10/2026): número do Pedido desta Oportunidade que está na Lixeira (fora de "pedidos") */
  pedido_na_lixeira?: string | null;
  /** P3-17: número do Pedido cancelado */
  pedido_cancelado?: string | null;
  ocs_cliente: string[];
  chaves: Chave[];
  migrado?: boolean;
  atualizado_em: string;
}
export interface Tarefa {
  id: string;
  titulo: string;
  data: string;
  concluida: boolean;
  concluida_em: string | null;
  responsavel_nome: string;
  concluida_por_nome: string | null;
  origem: string | null;
}
export interface Oportunidade extends OportunidadeResumo {
  programa: string | null;
  escopo: string | null;
  origem: { origem: string | null; campanha: string | null; indicacao: string | null; parceiro: string | null; observacao: string | null };
  entidades_cliente: { id: string; tipo: TipoEntidade; razao_social: string; cnpj: string; status: 'ativo' | 'inativo'; codigo?: string | null; principal?: boolean }[];
  contatos: { id: string; nome: string }[];
  tarefas: Tarefa[];
  atividades: { id: string; tipo: string; resumo: string; resultado: string | null; observacao: string | null; data_hora: string; autor: string | null; contato_nome: string | null }[];
  eventos: { texto: string; data_hora: string; autor: string | null }[];
  historico_legado?: HistoricoLegado;
  derivacoes?: { campo: string; regra: string; descricao: string }[];
  lote_migracao?: string | null;
}
/** Registro histórico da planilha (somente leitura; valores literais da origem). */
export interface HistoricoLegado {
  registros: {
    id: string;
    papel: 'proposta_historica' | 'alternativa';
    alternativa_de: string | null;
    proposta_numero: string;
    versao: number | null;
    tipo_origem: string;
    data_proposta: string | null;
    validade_dias: number | null;
    situacao_origem: string | null;
    forma_pagamento_texto: string | null;
    modalidade: string | null;
    prazo_meses: number | null;
    frete_texto: string | null;
    valor_frete_origem: number | null;
    parceiro: string | null;
    parceiro_cnpj_origem: string | null;
    contato: string | null;
    observacao: string | null;
    itens: { linha: number; pn: string | null; descricao: string | null; fabricante: string | null; tipo_item: string | null; quantidade: number | null; unidade: string | null; moeda: string | null; ptax: number | null; custo_cotado: number | null; custo_unit_reais: number | null; custo_linha: number | null; margem: number | null; venda_unit: number | null; total_linha: number | null; status: string | null; motivo: string | null }[];
  }[];
  contratos: { ped_legado: string; status_origem: string; data_pedido: string | null; oc_cliente: string | null; pedido_parceiro: string | null; parceiro: string | null; condicao: string | null; valor_total_origem: number | null; custo_total_origem: number | null; regra_receita: string | null; parcelas_efetivas: number | null }[];
}
export interface OpcoesOportunidade {
  hoje: string;
  estagios: { id: Estagio; nome: string; aberto: boolean }[];
  tipos: { id: Tipo; nome: string }[];
  motivos_perda: string[];
  tipos_atividade: string[];
  faixas_registro: FaixasRegistro | null;
  responsaveis: { id: string; nome: string }[];
  parceiros: { id: string; nome: string; papeis: string[] }[];
  parceiros_registro: { id: string; nome: string; papeis: string[] }[];
  fabricantes: { chave: string; nome: string; origem: 'parceiro' | 'catalogo'; parceiro_id?: string }[];
  motivos_encerramento_registro?: string[];
}
export const moeda = (v: number, casas = 0) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: casas, maximumFractionDigits: casas });
export const pct = (v: number) => `${(v * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
